import { useMemo, useState } from "react";
import { AlertTriangle, CalendarClock, CircleDollarSign } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { CustomerProduct } from "../../types";
import { daysUntil, formatMoney } from "../../utils/format";
import PageHeader from "../../components/ui/PageHeader";
import Card, { CardHeader } from "../../components/ui/Card";
import CustomerProductTable from "./CustomerProductTable";
import CustomerProductFormModal from "./CustomerProductFormModal";

const WINDOWS = [7, 30, 60, 90];

const sumRenewal = (rows: CustomerProduct[]) =>
    rows.reduce((s, r) => s + Number(r.renewalAmount ?? r.amount ?? 0), 0);

export default function RenewalsPage() {
    const { hasRole } = useAuth();
    const isAdmin = hasRole("SUPERADMIN");

    // One request for all software; overdue + upcoming are split client-side
    const { data, loading, error, reload } = useApi<CustomerProduct[]>("/customer-product", { type: "SOFTWARE" });
    const [windowDays, setWindowDays] = useState(30);
    const [editing, setEditing] = useState<CustomerProduct | null>(null);

    const { overdue, upcoming } = useMemo(() => {
        const live = (data ?? []).filter((r) => r.status !== "CANCELLED" && r.renewalDate);
        const withDays = live.map((r) => ({ r, d: daysUntil(r.renewalDate)! }));

        return {
            overdue: withDays.filter((x) => x.d < 0).sort((a, b) => a.d - b.d).map((x) => x.r),
            upcoming: withDays
                .filter((x) => x.d >= 0 && x.d <= windowDays)
                .sort((a, b) => a.d - b.d)
                .map((x) => x.r),
        };
    }, [data, windowDays]);

    const edit = isAdmin ? setEditing : undefined;

    return (
        <>
            <PageHeader title="Renewals" subtitle="Software subscriptions that are overdue or coming up for renewal." />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <MiniStat icon={AlertTriangle} tone="text-red-400" label="Overdue" value={data ? String(overdue.length) : "…"} />
                <MiniStat icon={CalendarClock} tone="text-amber-400" label={`Due in ${windowDays} days`} value={data ? String(upcoming.length) : "…"} />
                <MiniStat
                    icon={CircleDollarSign}
                    tone="text-emerald-400"
                    label="Expected renewals"
                    value={data ? formatMoney(sumRenewal([...overdue, ...upcoming])) : "…"}
                />
            </div>

            <div className="space-y-6">
                {(overdue.length > 0 || loading || error) && (
                    <Card padded={false}>
                        <div className="p-5 pb-0">
                            <CardHeader title="Overdue" subtitle="Renewal date has passed. Follow up or update the subscription." icon={AlertTriangle} />
                        </div>
                        <CustomerProductTable
                            rows={data ? overdue : null}
                            loading={loading}
                            error={error}
                            onRetry={reload}
                            onEdit={edit}
                            emptyTitle="Nothing overdue"
                        />
                    </Card>
                )}

                <Card padded={false}>
                    <div className="p-5 pb-0">
                        <CardHeader
                            title="Upcoming"
                            subtitle="Soonest first."
                            icon={CalendarClock}
                            action={
                                <div className="flex gap-1 p-1 rounded-xl bg-slate-950/50 border border-slate-800">
                                    {WINDOWS.map((w) => (
                                        <button
                                            key={w}
                                            onClick={() => setWindowDays(w)}
                                            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                                                windowDays === w ? "bg-[#8b5cf6] text-white" : "text-slate-400 hover:text-white"
                                            }`}
                                        >
                                            {w}d
                                        </button>
                                    ))}
                                </div>
                            }
                        />
                    </div>
                    <CustomerProductTable
                        rows={data ? upcoming : null}
                        loading={loading}
                        error={error}
                        onRetry={reload}
                        onEdit={edit}
                        emptyTitle={`No renewals in the next ${windowDays} days`}
                        emptyText="Try a longer window."
                    />
                </Card>
            </div>

            <CustomerProductFormModal
                open={!!editing}
                item={editing}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    reload();
                }}
            />
        </>
    );
}

function MiniStat({ icon: Icon, tone, label, value }: { icon: React.ElementType; tone: string; label: string; value: string }) {
    return (
        <Card className="flex items-center gap-4">
            <div className={`p-3 rounded-2xl bg-slate-900 border border-slate-700 ${tone}`}>
                <Icon size={20} />
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</p>
                <p className="text-xl font-bold text-white truncate">{value}</p>
            </div>
        </Card>
    );
}